/**
 * Pricing Summary Component
 *
 * @package Enterprise\BookingSystem\Blocks
 */

/**
 * WordPress dependencies
 */
import { useSelect } from '@wordpress/data';
import { __ } from '@wordpress/i18n';

/**
 * Internal dependencies
 */
import { store as addToCartStore } from '../../../data/add-to-cart';

type PricingSummaryProps = {
	readonly productId: number;
};

export const PricingSummary = ({ productId }: PricingSummaryProps) => {
	const { baseTotal, currency, fees, formattedVariantTotal, quantity, selectedVariant } =
		useSelect(
			(select) => {
				const storeSelectors = select(addToCartStore);
				const instance = storeSelectors.getInstance(productId);
				const variant = storeSelectors.getSelectedVariant(productId);
				const qty = storeSelectors.getQuantity(productId);

				const base = variant
					? variant.price_amount * qty
					: (instance?.price ?? 0) * qty;

				return {
					baseTotal: base,
					currency: instance?.currency ?? 'USD',
					fees: storeSelectors.getFees(productId),
					formattedVariantTotal: storeSelectors.getFormattedTotal(productId),
					quantity: qty,
					selectedVariant: variant,
				};
			},
			[productId],
		);

	const activeFees = fees.filter((fee) => fee.is_active);
	const feesTotal = activeFees.reduce((sum, fee) => sum + fee.amount, 0);
	const grandTotalCents = baseTotal + feesTotal;

	const formatCurrency = (amountInCents: number) =>
		new Intl.NumberFormat('en-US', {
			currency,
			style: 'currency',
		}).format(amountInCents / 100);

	return (
		<div className="ebs-add-to-cart__pricing">
			<div className="ebs-add-to-cart__pricing-breakdown">
				<div className="ebs-add-to-cart__pricing-row">
					<span className="ebs-add-to-cart__pricing-label">
						{selectedVariant
							? `${selectedVariant.name} × ${quantity}`
							: __('Base price', 'enterprise-booking-system')}
					</span>
					<span className="ebs-add-to-cart__pricing-value">{formattedVariantTotal}</span>
				</div>

				{activeFees.map((fee) => (
					<div key={fee.type} className="ebs-add-to-cart__pricing-row">
						<span className="ebs-add-to-cart__pricing-label">{fee.name}</span>
						<span className="ebs-add-to-cart__pricing-value">
							{formatCurrency(fee.amount)}
						</span>
					</div>
				))}

				{activeFees.some((fee) => fee.description) ? (
					<div className="ebs-add-to-cart__pricing-notes">
						{activeFees.map((fee) =>
							fee.description ? (
								<p key={fee.type} className="ebs-add-to-cart__pricing-note">
									{fee.description}
								</p>
							) : null,
						)}
					</div>
				) : null}
			</div>

			<div className="ebs-add-to-cart__pricing-total">
				<span className="ebs-add-to-cart__total-label">
					{__('Total', 'enterprise-booking-system')}
				</span>
				<span className="ebs-add-to-cart__total-value">{formatCurrency(grandTotalCents)}</span>
			</div>
		</div>
	);
};
